import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { useGetLookUpDropdownListQuery } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';

const WarrantyTypeDropdownWithMulti = ({
    value,
    onChange,
    className,
    disabled,
    multiple = false,
    showSelectAll = true,
    emitFilterPayload = false
}) => {
    const { getCurrentLanguageId, th } = useLanguage();
    const [selectedWarrantyType, setSelectedWarrantyType] = useState(multiple ? [] : null);

    const { data: warrantyTypeList = [], isLoading } = useGetLookUpDropdownListQuery({
        RowCount: 100,
        LanguageId: getCurrentLanguageId()
    });

    const warrantyTypeOptions = useMemo(() =>
        warrantyTypeList.map((type) => ({
            id: type.code,
            lookupId: type.id,
            code: type.code,
            text: type.translatedText,
            displayValue: type.translatedText
        })),
        [warrantyTypeList]
    );

    const selectAllOption = useMemo(() => ({
        id: 'select-all',
        code: 'select-all',
        text: th('dropdown.warrantyType.selectAll'),
        displayValue: th('dropdown.warrantyType.selectAll'),
        isSelectAll: true
    }), [th]);

    const enhancedOptions = useMemo(() =>
        multiple && showSelectAll ? [selectAllOption, ...warrantyTypeOptions] : warrantyTypeOptions,
        [multiple, showSelectAll, selectAllOption, warrantyTypeOptions]
    );

    const handleSelectionChange = (selectedValue) => {
        if (multiple) {
            const currentSelection = Array.isArray(selectedValue) ? selectedValue : [];
            let finalSelection = [];

            const selectAllOptionClicked = currentSelection.find(opt => opt.isSelectAll);

            if (selectAllOptionClicked) {
                const areAllSelected = selectedWarrantyType && selectedWarrantyType.length === warrantyTypeOptions.length;

                if (areAllSelected) {
                    finalSelection = [];
                } else {
                    finalSelection = [...warrantyTypeOptions];
                }
            } else {
                finalSelection = currentSelection.filter(opt => !opt.isSelectAll);
            }

            setSelectedWarrantyType(finalSelection);

            if (emitFilterPayload) {
                const allSelected = warrantyTypeOptions.length > 0 && finalSelection.length === warrantyTypeOptions.length;
                onChange({
                    isAllWarrTypes: allSelected,
                    warrTypeIds: allSelected ? [] : finalSelection.map(opt => opt.lookupId)
                });
            } else {
                onChange(finalSelection.map(opt => opt.id));
            }
        } else {
            setSelectedWarrantyType(selectedValue);
            onChange(selectedValue ? selectedValue.id : null);
        }
    };

    const itemTemplate = (option) => {
        if (!multiple) {
            return <div>{option.text}</div>;
        }

        const isSelected = selectedWarrantyType && selectedWarrantyType.some(sel => sel.id === option.id);
        const isSelectAllOption = option.isSelectAll;
        const areAllSelected = selectedWarrantyType && selectedWarrantyType.length === warrantyTypeOptions.length;

        return (
            <div className="flex align-items-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={isSelectAllOption ? areAllSelected : isSelected}
                    className="mr-2"
                    onChange={(e) => {
                        if (isSelectAllOption) {
                            if (areAllSelected) {
                                handleSelectionChange([]);
                            } else {
                                handleSelectionChange([...warrantyTypeOptions]);
                            }
                        } else {
                            const newSelection = [...(selectedWarrantyType || [])];
                            const optionIndex = newSelection.findIndex(sel => sel.id === option.id);

                            if (e.checked) {
                                if (optionIndex === -1) {
                                    newSelection.push(option);
                                }
                            } else {
                                if (optionIndex > -1) {
                                    newSelection.splice(optionIndex, 1);
                                }
                            }

                            handleSelectionChange(newSelection);
                        }
                    }}
                />
                {option.isSelectAll ? (
                    <span style={{ fontWeight: 'bold' }}>{option.displayValue}</span>
                ) : (
                    <div>{option.text}</div>
                )}
            </div>
        );
    };

    const valueTemplate = (option, props) => {
        const placeholder = th('dropdown.warrantyType.placeholder');

        if (!multiple) {
            return option ? option.displayValue : placeholder;
        }

        if (!selectedWarrantyType || selectedWarrantyType.length === 0) {
            return placeholder;
        }

        if (selectedWarrantyType.length === warrantyTypeOptions.length) {
            return th('dropdown.warrantyType.allSelected');
        }

        if (selectedWarrantyType.length === 1) {
            return selectedWarrantyType[0].displayValue;
        }
        return th('dropdown.warrantyType.typesSelected', { count: selectedWarrantyType.length });
    };

    useEffect(() => {
        if (!value) {
            setSelectedWarrantyType(multiple ? [] : null);
            return;
        }

        if (warrantyTypeOptions.length > 0) {
            if (multiple) {
                if (value?.isAllWarrTypes) {
                    setSelectedWarrantyType(warrantyTypeOptions);
                    return;
                }

                const isPayloadShape = !Array.isArray(value) && Array.isArray(value?.warrTypeIds);
                const rawValues = isPayloadShape ? value.warrTypeIds : Array.isArray(value) ? value : [value];
                const selectedOptions = rawValues
                    .map(val => warrantyTypeOptions.find(opt => (isPayloadShape ? opt.lookupId === val : opt.id === val)))
                    .filter(Boolean);

                setSelectedWarrantyType(selectedOptions);
            } else {
                const found = warrantyTypeOptions.find((option) => option.id === value);
                setSelectedWarrantyType(found || null);
            }
        }
    }, [value, warrantyTypeOptions, multiple]);

    return (
        <Dropdown
            value={selectedWarrantyType}
            options={enhancedOptions}
            onChange={(e) => handleSelectionChange(e.value)}
            optionLabel="displayValue"
            showClear
            className={className}
            placeholder={th('dropdown.warrantyType.placeholder')}
            loading={isLoading}
            disabled={disabled}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
            multiple={multiple}
        />
    );
};

export default WarrantyTypeDropdownWithMulti;